import { Directive, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { HttpErrorResponse } from '@angular/common/http';
// LIBS
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
// SERVICES
import { NodeService } from '@services/node/node.service';
import { AuthenticationConfigurationService } from '@services/authentication/authentication-configuration.service';
import { AlertService } from '@services/notification/alert.service';
// MODELS
import { Response } from '@models/response-api/ResponseApi';
import { Catalog } from '@models/catalog/Catalog';

@Directive({ selector: 'filter-node-base-pl' })
export abstract class FilterNodeBasePl implements OnDestroy {
	abstract isBachiller: boolean;
	isModuleCobach = false;
	subscription = new Subscription();
	form!: FormGroup;
	levels: Catalog[] = [];
	levelsElt: Catalog[] = [];
	levelsPlan: Catalog[] = [];
	systems: Catalog[] = [];
	systems_cobach: Catalog[] = [];
	grades: Catalog[] = [];
	signatures: Catalog[] = [];
	systemWithoutGrade = [
		'NEM Serie Trayectorias',
		'NEM Serie Perspectivas',
		'COBACH Chihuahua',
	];
	isSystemWithoutGrade = false;
	private filtersSubject: BehaviorSubject<any>;
	filtersObservable: Observable<any>;
	filters: any = {};
	nodeId: number;
	roleId = sessionStorage.getItem('role');
	constructor(
		private fb: FormBuilder,
		public nodeService: NodeService,
		public authConfService: AuthenticationConfigurationService,
		public alertService: AlertService
	) {
		const { subsystemId } = authConfService.getUser();
		this.nodeId = subsystemId;
		this.filters = { subsystemId, nodeId: this.nodeId };
		this.filtersSubject = new BehaviorSubject<any>(this.filters);
		this.filtersObservable = this.filtersSubject.asObservable();
		this.createForm();
		this.watchChangesLevel();
		this.watchChangesSystem();
		this.watchChangesGrade();
		this.watchChangesSignature();
		this.loadLevels();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	get controls() {
		return this.form.controls;
	}

	createForm() {
		this.form = this.fb.group({
			level: [null],
			grade: [{ value: null, disabled: true }],
			system: [{ value: null, disabled: true }],
			signature: [{ value: null, disabled: true }],
		});
	}

	isNodeBachiller() {
		const levelId = coerceNumberProperty(this.controls['level'].value, null);
		const levels = this.levels.filter((x) => x.id == levelId).map((x) => x.description);

		return levels.includes('Bachillerato');
	}

	isNodeBachillerValladolid() {
		const isSystemValladolid = this.authConfService.isSystemValladolid();
		return isSystemValladolid && this.isNodeBachiller();
	}

	showOnlySystemValladolid(systems: Catalog[]) {
		const systemAuthorize = ['NEM Serie Trayectorias', 'DGB'];
		return systems.filter((x) => systemAuthorize.includes(x.name));
	}

	// loading data everytime the user changes the filters
	loadLevels() {
		this.subscription.add(
			this.nodeService.getNodeNestedV2(this.nodeId).subscribe({
				next: (x: Response) => {
					x.success ? (this.levels = x.data as Catalog[]) : (this.levels = []);

					this.levelsPlan = this.levels.filter((obj) => obj.id === 975);

					this.levels = this.levels.filter((obj) => obj.id !== 3570);
					this.levels = this.levels.filter((obj) => obj.id !== 3531);
					this.levels = this.levels.filter((obj) => obj.id !== 975);

					this.levelsElt = this.levels.filter((obj) => obj.id !== 3602);
					this.levelsElt = this.levelsElt.filter((obj) => obj.id !== 3570);
					this.levelsElt = this.levelsElt.filter((obj) => obj.id !== 3531);
					this.selectDefaultLevelOrShowMessageLevel();
				},
				error: (error: HttpErrorResponse) => {
					this.alertService.showSmallErrorServer();
				},
			})
		);
	}

	selectDefaultLevelOrShowMessageLevel() {
		if (this.levels.length === 1) {
			this.controls['level'].setValue(this.levels[0].id);
		} else if (this.levels.length > 1) {
			this.showMessageSelectLevel();
		}
	}

	loadSystems(levelId: number) {
		this.subscription.add(
			this.nodeService.getNodeNestedV2(levelId).subscribe({
				next: (x: Response) => {
					const systemsAll = x.data as Catalog[];
					if (this.isNodeBachillerValladolid()) {
						this.systems = this.showOnlySystemValladolid(systemsAll);
					} else if (this.isModuleCobach) {
						if (Number(this.roleId) !== 89) {
							this.systems = systemsAll.filter((x) => x.id === 4445);
						} else {
							this.systems = systemsAll.filter((x) => x.id !== 4445 && x.id !== 4446);
						}
					} else {
						this.systems = systemsAll.filter((x) => x.id !== 4445 && x.id !== 4446);
					}
					this.selectDefaultSystem();
				},
				error: (error: HttpErrorResponse) => {
					this.alertService.showSmallErrorServer();
				},
			})
		);
	}

	selectDefaultSystem() {
		if (this.systems.length === 1) {
			this.controls['system'].setValue(this.systems[0].id);
		}
	}

	loadGrades(parentId: number) {
		this.subscription.add(
			this.nodeService.getNodeNestedV2(parentId).subscribe({
				next: (x: Response) => {
					x.success
						? (this.grades = (x.data as Catalog[]).filter((x) => x.name !== 'Plan Lector'))
						: (this.grades = []);
				},
				error: (error: HttpErrorResponse) => {
					this.alertService.showSmallErrorServer();
				},
			})
		);
	}

	loadSignatures(gradeId: number) {
		this.subscription.add(
			this.nodeService.getNodeNestedV2(gradeId).subscribe({
				next: (x: Response) => {
					x.success ? (this.signatures = x.data as Catalog[]) : (this.signatures = []);
				},
				error: (error: HttpErrorResponse) => {
					this.alertService.showSmallErrorServer();
				},
			})
		);
	}

	watchChangesLevel() {
		this.subscription.add(
			this.controls['level'].valueChanges.subscribe((levelId) => {
				let level = this.levels.filter((x) => x.id === Number(levelId));

				sessionStorage.setItem('filterContent', level[0].name);
				sessionStorage.setItem('filterOption', 'Nivel');

				this.handleChangeLevel(levelId);
				!levelId && this.showMessageSelectLevel();
			})
		);
	}

	handleChangeLevel(levelId: number) {
		if (this.isBachiller || this.isNodeBachillerValladolid()) {
			this.handleChangeLevelBachiller();
		} else {
			this.handleChangeLevelBasic();
		}
	}

	handleChangeLevelBachiller() {
		this.systems = [];
		const level = coerceNumberProperty(this.controls['level'].value, null);
		this.controls['system'].setValue(null, { emitEvent: false });
		this.controls['grade'].setValue(null, { emitEvent: false });
		if (level) {
			this.controls['system'].enable({ emitEvent: false });
			this.loadSystems(level);
		} else {
			this.controls['system'].disable({ emitEvent: false });
		}
		this.handleChangeSystem();
	}

	handleChangeLevelBasic() {
		this.grades = [];
		const level = coerceNumberProperty(this.controls['level'].value, null);
		this.controls['grade'].setValue(null, { emitEvent: false });
		this.controls['system'].setValue(null, { emitEvent: false });
		if (level) {
			this.controls['grade'].enable({ emitEvent: false });
			this.loadGrades(level);
		} else {
			this.controls['grade'].disable({ emitEvent: false });
		}
		this.handleChangeGrade();
	}

	watchChangesSystem() {
		this.subscription.add(
			this.controls['system'].valueChanges.subscribe(() => {
				const gradeId = this.controls['system'].value;
				let level = this.systems.filter((x) => x.id === Number(gradeId));
				sessionStorage.setItem('filterContent', level[0].name);
				sessionStorage.setItem('filterOption', 'Sistema');

				this.handleChangeSystem();
			})
		);
	}

	handleChangeSystem() {
		this.grades = [];
		const system = coerceNumberProperty(this.controls['system'].value, null);
		this.controls['grade'].setValue(null, { emitEvent: false });
		if (system) {
			this.controls['grade'].enable({ emitEvent: false });
			this.loadGrades(system);
		} else {
			this.controls['grade'].disable({ emitEvent: false });
		}
		this.handleChangeGrade();
	}

	watchChangesGrade() {
		this.subscription.add(
			this.controls['grade'].valueChanges.subscribe(() => {
				const gradeId = this.controls['grade'].value;
				let level = this.grades.filter((x) => x.id === Number(gradeId));
				sessionStorage.setItem('filterContent', level[0].name);
				sessionStorage.setItem('filterOption', 'Grado');
				this.handleChangeGrade();
			})
		);
	}

	handleChangeGrade() {
		this.signatures = [];
		const grade = coerceNumberProperty(this.controls['grade'].value, null);
		this.controls['signature'].setValue(null, { emitEvent: false });
		if (grade) {
			this.controls['signature'].enable({ emitEvent: false });
			this.loadSignatures(grade);
		} else {
			this.controls['signature'].disable({ emitEvent: false });
		}
		this.handleChangesSignature();
	}

	watchChangesSignature() {
		this.subscription.add(
			this.controls['signature'].valueChanges.subscribe(() => {
				const gradeId = this.controls['signature'].value;
				let level = this.signatures.filter((x) => x.id === Number(gradeId));
				sessionStorage.setItem('filterContent', level[0].name);
				sessionStorage.setItem('filterOption', 'Asignatura');

				this.handleChangesSignature();
			})
		);
	}

	handleChangesSignature() {
		this.updateSystemWithGrade();

		this.updateFilter();
	}

	updateSystemWithGrade() {
		const systemId = coerceNumberProperty(this.controls['system'].value, null);
		const system = this.systems.find((x) => x.id === systemId);
		this.isSystemWithoutGrade = this.systemWithoutGrade.includes(system?.description || '');
	}

	clearFilters() {
		this.controls['level'].setValue(null);
	}

	updateFilter() {
		const user = this.authConfService.getUser();
		let systemId = user.systemId;

		if (systemId === 2) {
			const menu = sessionStorage.getItem('menu_select');
			let system;
			const signature = coerceNumberProperty(this.controls['signature'].value, null);

			if (menu?.toLowerCase().includes('cobach chihuahua')) {
				system = coerceNumberProperty(this.controls['system'].value, null);
			}
			const grade = coerceNumberProperty(this.controls['grade'].value, null);
			const level = coerceNumberProperty(this.controls['level'].value, null);
			this.filters = {
				...this.filters,
				nodeId: (() => {
					if (level !== null && level !== undefined) {
						if (system !== null && system !== undefined) {
							if (grade !== null && grade !== undefined) {
								if (signature !== null && signature !== undefined) {
									return signature;
								}
								return grade;
							}
							return system;
						}
						return level;
					} else {
						return this.nodeId;
					}
				})(),
				...(system !== undefined && { system }),
			};

			this.filtersSubject.next(this.filters);
		} else {
			const signature = coerceNumberProperty(this.controls['signature'].value, null);

			const system = coerceNumberProperty(this.controls['system'].value, null);

			const grade = coerceNumberProperty(this.controls['grade'].value, null);
			const level = coerceNumberProperty(this.controls['level'].value, null);
			this.filters = {
				...this.filters,
				nodeId: signature || grade || level || system || this.nodeId,
			};

			this.filtersSubject.next(this.filters);
		}
	}

	abstract eventLayerModule(): void;

	showMessageSelectLevel() {
		this.alertService.showInfo('Para visualizar contenido seleccione un nivel');
		this.eventLayerModule();
	}
}
